import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Flex,
	Form,
	Input,
	Modal,
	Row,
	Select,
	Typography,
	notification,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import SignatureCanvas from "react-signature-canvas";
import { api } from "../utils/Api";
const { Text } = Typography;

const CHECK_ITEMS = {
	daily: [
		{ field: "emergencyStop", title: "Emergency Stop", input: "comment" },
		{ field: "waterDispensing", title: "Water Dispensing", input: "comment" },
		{
			field: "admixtureDispensing",
			title: "Admixture Dispensing",
			input: "comment",
		},
		{ field: "aggregateGates", title: "Aggregate Gates", input: "comment" },
		{
			field: "conveyorRotation",
			title: "Conveyor Rotation",
			input: "comment",
		},
	],
	weekly: [
		{
			field: "mainPower",
			title: "Main power is on and in good condition.",
			description: "If it is off, it must be turned on.",
			input: "comment",
		},
		{
			field: "controllerFunctional",
			title: "The controller is on and functioning properly.",
			description: "If it is off, it must be turned on.",
			input: "comment",
		},
		{
			field: "cpuFunctional",
			title: "The CPU is on and functioning properly.",
			description: "If it is off, it must be turned on.",
			input: "comment",
		},
		{
			field: "waterValves",
			title: "The water valves are properly open.",
			description: "Open and close the valves if necessary.",
			input: "comment",
		},
		{
			field: "airLeaks",
			title: "There are no air leaks in the pneumatic systems.",
			description: "Notify the plant maintenance department.",
			input: "comment",
		},
		{
			field: "materialGates",
			title: "The material gates have no damage.",
			description:
				"Check for visible damage and open and close gates to ensure proper operation.",
			input: "comment",
		},
		{
			field: "oilCompressor",
			title: "The compressor oil level is adequate.",
			description:
				"Refill oil if necessary and report to the maintenance team.",
			input: "comment",
		},
		{
			field: "airCompressorPressure",
			title: "Check air compressor pressure.",
			description: "Pressure should be between 125 - 130 psi.",
			input: "comment",
		},
		{
			field: "plantDispenses",
			title: "The plant dispenses water properly.",
			description: "Check water dispensing from the computer system.",
			input: "comment",
		},
		{
			field: "bandRotates",
			title: "The belt rotates properly.",
			description: "Rotate the belt from the computer system.",
			input: "comment",
		},
		{
			field: "chemicalLevel",
			title: "The chemical level is adequate to start the shift.",
			description:
				"If the level is low, report to the appropriate staff to request chemicals.",
			input: "comment",
		},
		{
			field: "reviewVibrators",
			title: "Check vibrators.",
			description: "Verify that the vibrators activate correctly.",
			input: "comment",
		},
		{
			field: "cementGate",
			title: "Check cement gate.",
			description: "Verify that the cement gates are open.",
			input: "comment",
		},
		{
			field: "humidityProcess",
			title:
				"Perform the humidity capture process and record humidity in the comments column.",
			description:
				"Perform the humidity capture process and record humidity in the comments column.",
			input: "percentage",
		},
	],
	monthly: [
		{
			field: "conveyorBearingsGrease",
			title: "Grease conveyor bearings",
			description: "Grease must be applied to the conveyor bearings",
			input: "comment",
		},
		{
			field: "binBearingsGrease",
			title: "Grease bin bearings",
			description: "Grease must be applied to the aggregate bin gate bearings",
			input: "comment",
		},
		{
			field: "pneumaticMaintenancePurge",
			title: "Purge pneumatic system maintenance unit",
			description:
				"Drain water from the maintenance unit of the pneumatic systems",
			input: "comment",
		},
		{
			field: "electrovalvesCleaning",
			title: "Clean electrovalves",
			description: "Perform cleaning of the pneumatic electrovalves",
			input: "comment",
		},
		{
			field: "pneumaticPistonsRodCleaning",
			title: "Clean pneumatic piston rods",
			description: "Perform cleaning on the pneumatic piston rods",
			input: "comment",
		},
		{
			field: "airFiltersCleaning",
			title: "Clean air filters",
			description: "Perform cleaning of the compressor air filters",
			input: "comment",
		},
		{
			field: "loadingChuteCleaning",
			title: "Clean loading chute",
			description: "Perform cleaning of the loading chute",
			input: "comment",
		},
		{
			field: "binVibrators",
			title: "Check bin vibrators",
			description: "Check that the bin vibrators work properly",
			input: "comment",
		},
		{
			field: "binGatesAlignment",
			title: "Check bin gate alignment",
			description: "Check the alignment of the bin gates",
			input: "comment",
		},
		{
			field: "conveyorBeltAlignment",
			title: "Check conveyor belt alignment",
			description: "Check the alignment of the conveyor belt",
			input: "comment",
		},
		{
			field: "beltTension",
			title: "Check belt tension",
			description: "Verify that the belt tension is adequate",
			input: "comment",
		},
		{
			field: "transmissionPulleys",
			title: "Check transmission pulleys",
			description:
				"Verify that the transmission pulleys that rotate the belt are in good condition",
			input: "comment",
		},
		{
			field: "dustCollectorOperation",
			title: "Check dust collector operation",
			description: "Verify that the dust collector works properly",
			input: "comment",
		},
		{
			field: "siloFullAlarm",
			title: "Check silo full alarm",
			description: "Verify that the silo full alarm works properly",
			input: "comment",
		},
		{
			field: "emergencyStops",
			title: "Check emergency stops",
			description: "Verify that emergency stops perform their function",
			input: "comment",
		},
		{
			field: "waterPump",
			title: "Check water pump",
			description: "Verify proper operation of the water pump",
			input: "comment",
		},
		{
			field: "admixturePump",
			title: "Check admixture pump",
			description: "Verify proper operation of the admixture pump",
			input: "comment",
		},
		{
			field: "pneumaticGates",
			title: "Check pneumatic gates",
			description: "Verify pneumatic gates of the plant",
			input: "comment",
		},
		{
			field: "scaleCalibrationCheck",
			title: "Check scale calibration with graduated tare weights (CIDC-006)",
			description:
				"Verify that the scales indicate the weight referenced to the graduated tare weights",
			input: "comment",
		},
		{
			field: "dustEmissionCheck",
			title: "Check dust emissions",
			description:
				"Verify dust emissions around the plant perimeter for 30 seconds in 6 minutes",
			input: "dust",
		},
	],
};

const TITLES = {
	daily: "Daily",
	weekly: "Weekly",
	monthly: "Monthly",
};

// Users allowed to create production checklists
const CREATE_ALLOWED_EMAILS = [
	"bpa@57concrete.com",
	"jp@57concrete.com",
	"ec@57concrete.com",
	"eliud@gmail.com",
];

export const canCreateProductionChecklist = () =>
	CREATE_ALLOWED_EMAILS.includes(localStorage.getItem("email"));

const DUST_EMISSION_FIELDS = [
	{ name: "dustEmissionCheckNorth", label: "North" },
	{ name: "dustEmissionCheckSouth", label: "South" },
	{ name: "dustEmissionCheckEast", label: "East" },
];

const ProductionChecklistNewModal = ({ type, open, onClose, onSuccess }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [plantOptions, setPlantOptions] = useState([]);
	const [subplantOptions, setSubplantOptions] = useState([]);
	const [loadingSubplants, setLoadingSubplants] = useState(false);
	const [responsibleOptions, setResponsibleOptions] = useState([]);
	const [loadingResponsibles, setLoadingResponsibles] = useState(false);
	const signaturePadRef = useRef(null);
	const [apiNotification, contextHolder] = notification.useNotification();
	const checkItems = CHECK_ITEMS[type];

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	useEffect(() => {
		if (open) {
			getPlants();
			getResponsibles();
		} else setSubplantOptions([]);
	}, [open]);

	const getResponsibles = async () => {
		const url = api + "/user/production/list";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingResponsibles(true);
			const { data } = await axios.get(url);
			if (data.success) {
				setResponsibleOptions(
					data.payload.map((item) => ({
						label: item.name,
						value: item._id,
					})),
				);
			} else {
				openNotification(
					"error",
					"Error",
					data.message || "Error while trying to get responsible users",
				);
			}
		} catch (err) {
			console.error(err.message);
			openNotification(
				"error",
				"Error",
				"Error while trying to get responsible users",
			);
		} finally {
			setLoadingResponsibles(false);
		}
	};

	const getPlants = async () => {
		const url = api + "/plant/list/57Concrete";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			setPlantOptions(
				data.payload.map((item) => ({
					label: item.name,
					value: item._id,
				})),
			);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get plants");
		}
	};

	const getSubplants = async (plant) => {
		const url = api + `/plant/${plant}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingSubplants(true);
			const { data } = await axios.get(url);
			setSubplantOptions(
				data.subplants
					.filter((item) => item.active)
					.map((item) => ({
						label: item.name,
						value: item.name,
					})),
			);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get subplants");
		} finally {
			setLoadingSubplants(false);
		}
	};

	const onValuesChange = (changedValues) => {
		// OK / NOK are mutually exclusive
		Object.entries(changedValues).forEach(([key, value]) => {
			if (key === "plant") {
				form.setFieldValue("subplant", undefined);
				setSubplantOptions([]);
				if (value) getSubplants(value);
				return;
			}
			if (!value) return;
			if (key.endsWith("NOK")) {
				form.setFieldValue(key.slice(0, -3) + "OK", false);
			} else if (key.endsWith("OK")) {
				form.setFieldValue(key.slice(0, -2) + "NOK", false);
			}
		});
	};

	const handleClearSignature = () => {
		signaturePadRef.current.clear();
	};

	const getCanvasWithWhiteBackground = (canvas) => {
		const newCanvas = document.createElement("canvas");
		const context = newCanvas.getContext("2d");

		newCanvas.width = canvas.width;
		newCanvas.height = canvas.height;

		context.fillStyle = "#fff";
		context.fillRect(0, 0, newCanvas.width, newCanvas.height);
		context.drawImage(canvas, 0, 0);

		return newCanvas;
	};

	const getSignatureFile = () => {
		const originalCanvas = signaturePadRef.current.getTrimmedCanvas();
		const canvasWithWhiteBackground =
			getCanvasWithWhiteBackground(originalCanvas);
		const base64 = canvasWithWhiteBackground.toDataURL("image/png");

		const byteString = atob(base64.split(",")[1]);
		const ab = new ArrayBuffer(byteString.length);
		const ia = new Uint8Array(ab);
		for (let i = 0; i < byteString.length; i++) {
			ia[i] = byteString.charCodeAt(i);
		}
		return new File([ab], "signature.png", { type: "image/png" });
	};

	const onFinish = async (values) => {
		const missingItems = checkItems.filter(
			(item) => !values[`${item.field}OK`] && !values[`${item.field}NOK`],
		);
		if (missingItems.length > 0) {
			openNotification(
				"warning",
				"Incomplete checklist",
				`Select OK or NOK for: ${missingItems
					.map((item) => item.title)
					.join(", ")}`,
			);
			return;
		}

		if (signaturePadRef.current.isEmpty()) {
			openNotification(
				"warning",
				"Signature required",
				"Please provide the customer signature before submitting.",
			);
			return;
		}

		const formData = new FormData();
		formData.append("type", type);
		formData.append("date", values.date.toDate().toISOString());
		formData.append("plant", values.plant);
		formData.append("subplant", values.subplant);
		formData.append("responsible", values.responsible);

		checkItems.forEach((item) => {
			formData.append(item.field, values[`${item.field}OK`] ? "true" : "false");
			if (item.input === "comment") {
				formData.append(
					`${item.field}Comment`,
					values[`${item.field}Comment`] || "",
				);
			}
			if (item.input === "percentage") {
				const percentage = values[`${item.field}Percentage`];
				if (
					percentage !== undefined &&
					percentage !== null &&
					percentage !== ""
				)
					formData.append(`${item.field}Percentage`, percentage);
			}
			if (item.input === "dust") {
				DUST_EMISSION_FIELDS.forEach((dustField) => {
					formData.append(dustField.name, values[dustField.name] || "");
				});
			}
		});

		formData.append("signature", getSignatureFile());

		try {
			setLoading(true);
			const url = api + "/production-new/post/jap";
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
			const config = {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			};

			const { data } = await axios.post(url, formData, config);
			if (data.success) {
				form.resetFields();
				signaturePadRef.current.clear();
				onSuccess(data.message);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification(
				"error",
				"Error",
				`Error while trying to create ${type} checklist`,
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Modal
			title={`New Production ${TITLES[type]} Checklist`}
			open={open}
			onCancel={onClose}
			width={1000}
			destroyOnClose
			maskClosable={false}
			afterOpenChange={(isOpen) => {
				// Recalculate canvas size once the modal animation is done
				if (isOpen && signaturePadRef.current) {
					signaturePadRef.current._resizeCanvas();
				}
			}}
			footer={[
				<Button key="cancel" size="large" onClick={onClose} disabled={loading}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					size="large"
					loading={loading}
					onClick={() => form.submit()}
				>
					Save
				</Button>,
			]}
		>
			{contextHolder}
			<Form
				form={form}
				layout="vertical"
				style={{ marginTop: "20px" }}
				onValuesChange={onValuesChange}
				onFinish={onFinish}
				preserve={false}
			>
				<Row gutter={16}>
					<Col span={24} md={12} lg={6}>
						<Form.Item
							name="date"
							label="Date"
							initialValue={dayjs()}
							rules={[{ required: true, message: "Date is required" }]}
						>
							<DatePicker
								size="large"
								showTime={{ format: "hh:mm A" }}
								format="MM/DD/YYYY hh:mm A"
								allowClear={false}
								style={{ width: "100%" }}
							/>
						</Form.Item>
					</Col>
					<Col span={24} md={12} lg={6}>
						<Form.Item
							name="plant"
							label="Plant"
							rules={[{ required: true, message: "Plant is required" }]}
						>
							<Select
								size="large"
								options={plantOptions}
								placeholder="Select plant"
								showSearch
								optionFilterProp="label"
							/>
						</Form.Item>
					</Col>
					<Col span={24} md={12} lg={6}>
						<Form.Item
							name="subplant"
							label="Subplant"
							rules={[{ required: true, message: "Subplant is required" }]}
						>
							<Select
								size="large"
								options={subplantOptions}
								placeholder="Select subplant"
								loading={loadingSubplants}
								disabled={subplantOptions.length === 0}
							/>
						</Form.Item>
					</Col>
					<Col span={24} md={12} lg={6}>
						<Form.Item
							name="responsible"
							label="Responsible"
							rules={[{ required: true, message: "Responsible is required" }]}
						>
							<Select
								size="large"
								options={responsibleOptions}
								placeholder="Select responsible"
								loading={loadingResponsibles}
								showSearch
								optionFilterProp="label"
							/>
						</Form.Item>
					</Col>
				</Row>
				<Row gutter={16} style={{ paddingBottom: 20, paddingTop: 20 }}>
					<Col span={24}>
						<Row gutter={16} justify={"space-between"}>
							<Col span={8}>
								<Text strong>Description / Additional Description</Text>
							</Col>
							<Col span={8}>
								<Text strong>Comments</Text>
							</Col>
							<Col>
								<Row gutter={16} justify={"end"}>
									<Col>
										<Text strong>OK</Text>
									</Col>
									<Col>
										<Text strong>NOK</Text>
									</Col>
								</Row>
							</Col>
						</Row>
					</Col>
				</Row>
				<Row gutter={16}>
					{checkItems.map((item) => (
						<ItemCheck key={item.field} {...item} />
					))}
				</Row>
				<Row gutter={16}>
					<Col span={24}>
						<Form.Item label="Customer Signature" required>
							<div
								style={{
									border: "1px solid #d9d9d9",
									padding: 10,
									borderRadius: 4,
								}}
							>
								<SignatureCanvas
									ref={signaturePadRef}
									penColor="black"
									backgroundColor="rgba(255,255,255,1)"
									canvasProps={{
										style: { width: "100%", height: 250, display: "block" },
										className: "sigCanvas",
									}}
								/>
							</div>
							<Button
								style={{ marginTop: 10 }}
								htmlType="button"
								onClick={handleClearSignature}
							>
								Clear Signature
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</Modal>
	);
};

export default ProductionChecklistNewModal;

const ItemCheck = ({ field, title, description, input }) => {
	return (
		<Col span={24} style={{ paddingTop: input === "dust" ? 16 : 0 }}>
			<Row gutter={16} justify={"space-between"}>
				<Col span={8}>
					<Row>
						<Col span={24}>
							<Text strong>{title}</Text>
						</Col>
						<Col span={24}>
							<Text strong type="secondary">
								{description}
							</Text>
						</Col>
					</Row>
				</Col>
				<Col span={8}>
					{input === "comment" && (
						<Form.Item name={`${field}Comment`}>
							<Input.TextArea size="large" />
						</Form.Item>
					)}
					{input === "percentage" && (
						<Form.Item name={`${field}Percentage`}>
							<Input size="large" type="number" suffix={"%"} />
						</Form.Item>
					)}
					{input === "dust" && (
						<Flex vertical>
							{DUST_EMISSION_FIELDS.map((dustField) => (
								<Form.Item
									key={dustField.name}
									name={dustField.name}
									label={dustField.label}
								>
									<Input.TextArea size="large" />
								</Form.Item>
							))}
						</Flex>
					)}
				</Col>
				<Col>
					<Row gutter={16}>
						<Col>
							<Form.Item
								name={`${field}OK`}
								valuePropName="checked"
								initialValue={false}
							>
								<Checkbox />
							</Form.Item>
						</Col>
						<Col>
							<Form.Item
								name={`${field}NOK`}
								valuePropName="checked"
								initialValue={false}
							>
								<Checkbox />
							</Form.Item>
						</Col>
					</Row>
				</Col>
			</Row>
		</Col>
	);
};
